import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { assetUrl } from "../../utils/api";
import { getUserId, planetGet, planetPost } from "../../api/planetApi";
import { destroy, drawRoute, fitBounds, initMap } from "../../planet/mapAdapter";

const STATUS_TEXT = {
  pending_audit: "正在审核，请稍候",
  rejected: "审核未通过",
  queued: "已通过审核，等待合适收件人",
  drifting: "漂流途中",
  pending_claim: "已送达，等待领取",
  read: "对方已打开",
  replied: "已有回信",
  closed: "对话已结束",
  expired: "已过期",
};

export default function PlanetBottleDetailPage() {
  const { id } = useParams();
  const userId = getUserId();
  const navigate = useNavigate();
  const [bottle, setBottle] = useState(null);
  const [reply, setReply] = useState("");
  const [msg, setMsg] = useState("");
  const mapRef = useRef(null);
  const adapterRef = useRef(null);

  const load = async () => {
    if (!userId) return;
    try {
      const data = await planetGet(`/bottles/${id}?user_id=${userId}`);
      setBottle(data.bottle);
    } catch (e) {
      setMsg(e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, userId]);

  useEffect(() => {
    if (!bottle || !mapRef.current) return;
    if (adapterRef.current) {
      destroy(adapterRef.current);
      adapterRef.current = null;
    }
    const adapter = initMap(mapRef.current, {
      center: [bottle.origin_lat || 31.2, bottle.origin_lng || 117.3],
      zoom: 5,
    });
    adapterRef.current = adapter;
    const pts = [];
    if (bottle.origin_lat != null) pts.push({ lat: bottle.origin_lat, lng: bottle.origin_lng });
    if (bottle.dest_lat != null) pts.push({ lat: bottle.dest_lat, lng: bottle.dest_lng });
    if (pts.length >= 2) {
      drawRoute(adapter, pts);
      fitBounds(adapter, pts);
    } else if (pts.length === 1) {
      adapter.map.setView([pts[0].lat, pts[0].lng], 6);
    }
    return () => {
      destroy(adapter);
      adapterRef.current = null;
    };
  }, [bottle]);

  if (!userId) {
    return (
      <p className="text-sm">
        请先 <Link to="/login" className="text-teal-700 underline">登录</Link>
      </p>
    );
  }

  const isRecipient = bottle && bottle.dest_user_id === userId;
  const isSender = bottle && bottle.sender_id === userId;
  const peerId = isSender ? bottle?.dest_user_id : bottle?.sender_id;

  const claim = async () => {
    try {
      const data = await planetPost(`/bottles/${id}/claim`, { user_id: userId });
      setBottle(data.bottle);
      setMsg("已领取并打开");
    } catch (e) {
      setMsg(e.message);
    }
  };

  const reject = async () => {
    try {
      await planetPost(`/bottles/${id}/reject`, { user_id: userId });
      setMsg("已拒绝，瓶子将尝试送给其他人");
      load();
    } catch (e) {
      setMsg(e.message);
    }
  };

  const sendReply = async () => {
    try {
      const data = await planetPost(`/bottles/${id}/reply`, {
        user_id: userId,
        content: reply,
      });
      setBottle(data.bottle);
      setReply("");
      setMsg("回复已发送");
    } catch (e) {
      setMsg(e.message);
    }
  };

  const invite = async () => {
    if (!peerId) return;
    try {
      await planetPost("/invites", {
        user_id: userId,
        to_user_id: peerId,
        bottle_id: Number(id),
      });
      setMsg("认识邀请已发送，等待对方同意后可进入私信");
    } catch (e) {
      setMsg(e.message);
    }
  };

  const report = async () => {
    try {
      await planetPost("/reports", {
        user_id: userId,
        bottle_id: Number(id),
        target_user_id: peerId || null,
        reason: "inappropriate",
        detail: "用户举报漂流瓶",
      });
      setMsg("已提交举报");
    } catch (e) {
      setMsg(e.message);
    }
  };

  const blockPeer = async () => {
    if (!peerId) return;
    try {
      await planetPost("/blocks", { user_id: userId, blocked_user_id: peerId });
      setMsg("已拉黑对方");
    } catch (e) {
      setMsg(e.message);
    }
  };

  if (!bottle && !msg) {
    return <p className="text-sm text-slate-500">加载中…</p>;
  }

  return (
    <div className="space-y-5">
      <button type="button" className="text-sm text-teal-700" onClick={() => navigate(-1)}>
        ← 返回
      </button>

      {bottle && (
        <>
          <div className="bg-white border rounded-xl p-4 space-y-2">
            <h2 className="text-lg font-medium text-teal-950">漂流瓶 #{bottle.id}</h2>
            <p className="text-sm text-slate-600">
              {bottle.origin_city || "?"} → {bottle.dest_city || "待定"} ·{" "}
              {STATUS_TEXT[bottle.status] || bottle.status}
            </p>
            {bottle.audit_note && isSender && (
              <p className="text-xs text-amber-700">审核备注：{bottle.audit_note}</p>
            )}
          </div>

          <div className="rounded-xl overflow-hidden border bg-slate-100">
            <div ref={mapRef} style={{ height: 220, width: "100%" }} />
            <p className="text-xs text-slate-500 px-3 py-2 bg-white">
              静态路线示意（模糊坐标），不代表实时轨迹
            </p>
          </div>

          {isRecipient && bottle.status === "pending_claim" && (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={claim}
                className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm"
              >
                领取并阅读
              </button>
              <button type="button" onClick={reject} className="px-4 py-2 rounded-lg border text-sm">
                拒绝
              </button>
            </div>
          )}

          {(bottle.can_read_content || (bottle.can_read && bottle.messages?.length)) && (
            <div className="space-y-3">
              {(bottle.messages || []).map((m) => (
                <div key={m.id} className="bg-white border rounded-xl p-4">
                  <p className="text-xs text-slate-400 mb-1">
                    {m.round_no === 0 ? "首信" : `第 ${m.round_no} 轮`} · 用户 {m.sender_id}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{m.content}</p>
                  {m.image_path && (
                    <img
                      src={assetUrl(m.image_path)}
                      alt="手写信"
                      className="mt-3 max-h-64 rounded-lg border"
                    />
                  )}
                </div>
              ))}

              {["read", "replied", "pending_claim"].includes(bottle.status) === false &&
                null}
              {(isSender || isRecipient) &&
                ["read", "replied"].includes(bottle.status) &&
                (bottle.reply_rounds || 0) < 3 && (
                  <div className="bg-white border rounded-xl p-4 space-y-2">
                    <textarea
                      className="w-full border rounded-lg px-3 py-2 text-sm min-h-[80px]"
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      placeholder="写回复（最多 3 轮）"
                    />
                    <button
                      type="button"
                      onClick={sendReply}
                      className="px-4 py-2 rounded-lg bg-teal-700 text-white text-sm"
                    >
                      发送回复
                    </button>
                  </div>
                )}

              {peerId && (isSender || isRecipient) && (
                <div className="flex flex-wrap gap-2 text-sm">
                  <button
                    type="button"
                    onClick={invite}
                    className="px-3 py-1.5 rounded-lg bg-slate-800 text-white"
                  >
                    发送认识邀请
                  </button>
                  <Link to={`/user/${peerId}`} className="px-3 py-1.5 rounded-lg border">
                    查看资料
                  </Link>
                  <button type="button" onClick={report} className="px-3 py-1.5 rounded-lg border text-rose-700">
                    举报
                  </button>
                  <button type="button" onClick={blockPeer} className="px-3 py-1.5 rounded-lg border text-rose-700">
                    拉黑
                  </button>
                </div>
              )}
            </div>
          )}

          {!bottle.can_read && bottle.status !== "pending_claim" && (
            <p className="text-sm text-slate-500">当前状态暂不可阅读正文（非收件人或未送达）。</p>
          )}
        </>
      )}

      {msg && <p className="text-sm text-teal-800">{msg}</p>}

      <InvitesPanel userId={userId} />
    </div>
  );
}

function InvitesPanel({ userId }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!userId) return;
    planetGet(`/invites?user_id=${userId}`)
      .then(setData)
      .catch(() => {});
  }, [userId]);

  if (!data?.incoming?.length) return null;

  const respond = async (inviteId, accept) => {
    const res = await planetPost(`/invites/${inviteId}/respond`, {
      user_id: userId,
      accept,
    });
    if (accept && res.peer_user_id) {
      localStorage.setItem(
        "current_chat_user",
        JSON.stringify({ id: res.peer_user_id, name: `用户${res.peer_user_id}` })
      );
      navigate("/chat", {
        state: { user: { id: res.peer_user_id, name: `用户${res.peer_user_id}` } },
      });
    } else {
      const fresh = await planetGet(`/invites?user_id=${userId}`);
      setData(fresh);
    }
  };

  return (
    <div className="bg-teal-50 border border-teal-100 rounded-xl p-4 space-y-2">
      <h3 className="text-sm font-medium text-teal-950">待处理的认识邀请</h3>
      {data.incoming
        .filter((i) => i.status === "pending")
        .map((i) => (
          <div key={i.id} className="flex items-center justify-between text-sm gap-2">
            <span>来自用户 {i.from_user_id}</span>
            <span className="flex gap-2">
              <button
                type="button"
                className="px-2 py-1 rounded bg-teal-700 text-white"
                onClick={() => respond(i.id, true)}
              >
                同意并私信
              </button>
              <button
                type="button"
                className="px-2 py-1 rounded border"
                onClick={() => respond(i.id, false)}
              >
                拒绝
              </button>
            </span>
          </div>
        ))}
    </div>
  );
}
